import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Send, Trash2, Search, X } from "lucide-react";

function StatusBadge({ status }) {
  return (
    <span
      className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full border ${
        status === "published"
          ? "border-[#C05A46]/40 text-[#C05A46] bg-[#C05A46]/8"
          : "border-border text-[#786B63] bg-[#F9F6F0]"
      }`}
    >
      {status === "published" ? "Published" : "Draft"}
    </span>
  );
}

export default function AdminListsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", intro: "", hero_image_url: "", cafe_ids: [] });
  const [saving, setSaving] = useState(false);

  // café search for picker
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    if (user === false) navigate("/login", { state: { from: "/admin/lists" } });
    else if (user && user.role !== "admin") navigate("/", { replace: true });
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/lists");
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user && user.role === "admin") load(); }, [user]);

  useEffect(() => {
    if (!searchQ.trim()) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await api.get("/cafes", { params: { q: searchQ } });
      setSearchResults(data.slice(0, 8));
    }, 220);
    return () => clearTimeout(t);
  }, [searchQ]);

  const openNew = () => {
    setEditing(null);
    setForm({ title: "", intro: "", hero_image_url: "", cafe_ids: [] });
    setShowCompose(true);
  };

  const openEdit = (l) => {
    setEditing(l);
    setForm({
      title: l.title, intro: l.intro,
      hero_image_url: l.hero_image_url || "",
      cafe_ids: l.cafe_ids || [],
    });
    setShowCompose(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/lists/${editing.id}`, form);
        toast("List updated.");
      } else {
        await api.post("/admin/lists", form);
        toast("Draft list saved.");
      }
      setShowCompose(false);
      await load();
    } catch (e) {
      toast("Couldn't save", { description: formatApiError(e.response?.data?.detail) || e.message });
    } finally { setSaving(false); }
  };

  const togglePublish = async (l) => {
    try {
      await api.post(`/admin/lists/${l.id}/publish`);
      await load();
      toast(l.status === "published" ? "Unpublished." : "Published — now live.");
    } catch (e) {
      toast("Couldn't toggle", { description: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete list "${l.title}"?`)) return;
    try {
      await api.delete(`/admin/lists/${l.id}`);
      await load();
      toast("Deleted.");
    } catch (e) {
      toast("Couldn't delete", { description: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  const addCafe = (c) => {
    if (form.cafe_ids.includes(c.id)) return;
    setForm({ ...form, cafe_ids: [...form.cafe_ids, c.id] });
    setSearchQ("");
    setSearchResults([]);
  };
  const removeCafe = (id) => setForm({ ...form, cafe_ids: form.cafe_ids.filter((x) => x !== id) });
  const moveCafe = (id, dir) => {
    const arr = [...form.cafe_ids];
    const i = arr.indexOf(id);
    if (i < 0) return;
    const j = dir === "up" ? i - 1 : i + 1;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setForm({ ...form, cafe_ids: arr });
  };

  if (user && user.role !== "admin") return null;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 pb-24" data-testid="admin-lists-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#786B63]">Editor desk</p>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">Lists & guides</h1>
        </div>
        <Button onClick={openNew} className="rounded-sm bg-[#C05A46] hover:bg-[#A34A38] text-[#F9F6F0]" data-testid="new-list-btn">
          <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> New list
        </Button>
      </div>

      <div className="mt-10 space-y-4">
        {loading ? (
          [...Array(2)].map((_, i) => <div key={i} className="border border-border bg-card rounded-sm h-24 animate-pulse" />)
        ) : items.length === 0 ? (
          <div className="border border-border bg-card rounded-sm p-10 text-center">
            <p className="font-serif text-2xl">No lists yet.</p>
            <p className="text-sm text-[#786B63] mt-1">Compose your first guide.</p>
          </div>
        ) : items.map((l) => (
          <div key={l.id} data-testid={`list-row-${l.id}`} className="border border-border bg-card rounded-sm p-5 grid md:grid-cols-[1fr_auto] gap-4 items-center">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-serif text-2xl">{l.title}</h3>
                <StatusBadge status={l.status} />
                <span className="text-xs text-[#786B63]">{(l.cafe_ids || []).length} cafés</span>
              </div>
              <p className="text-sm text-[#786B63] mt-1 line-clamp-1">{l.intro}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => openEdit(l)} data-testid={`edit-list-${l.id}`}>Edit</Button>
              <Button
                size="sm"
                className="rounded-sm bg-[#2C1E16] hover:bg-[#1d130d] text-[#F9F6F0]"
                onClick={() => togglePublish(l)}
                data-testid={`publish-list-${l.id}`}
              >
                <Send className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                {l.status === "published" ? "Unpublish" : "Publish"}
              </Button>
              <Button variant="outline" size="sm" className="rounded-sm text-[#C05A46]" onClick={() => remove(l)} data-testid={`delete-list-${l.id}`}>
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showCompose} onOpenChange={setShowCompose}>
        <DialogContent className="max-w-3xl bg-[#F9F6F0] border-border rounded-sm" data-testid="list-compose-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">{editing ? "Edit list" : "New list"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 rounded-sm bg-[#F2EBE1] h-11" data-testid="list-title" />
            </div>
            <div>
              <Label>Intro *</Label>
              <Textarea rows={3} value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} className="mt-1.5 rounded-sm bg-[#F2EBE1]" data-testid="list-intro" />
            </div>
            <div>
              <Label>Hero image URL</Label>
              <Input value={form.hero_image_url} onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })} className="mt-1.5 rounded-sm bg-[#F2EBE1] h-11" placeholder="https://…" />
            </div>
            <div>
              <Label>Cafés ({form.cafe_ids.length})</Label>
              <div className="mt-2 border border-border bg-[#F2EBE1] rounded-sm p-3 space-y-2">
                {form.cafe_ids.length === 0 && <p className="text-xs text-[#786B63]">Add cafés below — order matters.</p>}
                {form.cafe_ids.map((id, i) => (
                  <div key={id} className="flex items-center gap-2 text-sm">
                    <span className="font-serif text-lg text-[#C05A46] w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <span className="flex-1 truncate">{id}</span>
                    <button type="button" onClick={() => moveCafe(id, "up")} className="text-xs px-2 py-0.5 border border-border rounded-sm">↑</button>
                    <button type="button" onClick={() => moveCafe(id, "down")} className="text-xs px-2 py-0.5 border border-border rounded-sm">↓</button>
                    <button type="button" onClick={() => removeCafe(id)} className="text-xs text-[#C05A46]"><X size={14} /></button>
                  </div>
                ))}
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-[#786B63]" strokeWidth={1.5} />
                  <Input
                    value={searchQ}
                    onChange={(e) => setSearchQ(e.target.value)}
                    placeholder="Search a café to add…"
                    className="pl-8 rounded-sm bg-[#F9F6F0] h-10"
                    data-testid="list-cafe-search"
                  />
                  {searchResults.length > 0 && (
                    <div className="absolute z-10 left-0 right-0 top-full mt-1 border border-border bg-[#F9F6F0] rounded-sm max-h-56 overflow-auto">
                      {searchResults.map((c) => (
                        <button key={c.id} type="button" onClick={() => addCafe(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-[#F2EBE1]">
                          <span className="text-[#2C1E16]">{c.name}</span>
                          <span className="text-[#786B63] text-xs"> · {c.city}, {c.country}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setShowCompose(false)}>Cancel</Button>
            <Button
              className="rounded-sm bg-[#2C1E16] hover:bg-[#1d130d] text-[#F9F6F0]"
              onClick={save}
              disabled={saving || !form.title || form.intro.length < 10}
              data-testid="list-save"
            >
              {saving ? "Saving…" : editing ? "Update list" : "Save draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
